import { Mutex } from 'async-mutex'
import { AxiosError, InternalAxiosRequestConfig } from 'axios'

import type { ApisauceInstance } from '@mangovoice/apisauce'

import type { AuthInterceptorConfig } from '../types'

const INVALID_AUTH_MESSAGE_DEFAULT = 'Your authentication is invalid.'

const hasInvalidAuthMessage = (data: unknown, message: string): boolean => {
  if (!data || typeof data !== 'object') return false
  return (
    'message' in data && (data as { message?: unknown }).message === message
  )
}

/**
 * Creates an auth interceptor pair (request + response) bound to a specific
 * apisauce instance. Retries go through the instance's own axios instance so
 * all other interceptors/transforms are applied. A `__isAuthRetry` flag
 * prevents infinite retry loops.
 */
export const createAuthInterceptor = (
  instance: ApisauceInstance,
  config: AuthInterceptorConfig,
  sharedMutex?: Mutex,
) => {
  const mutex = sharedMutex ?? new Mutex()
  const invalidAuthMessage =
    config.invalidAuthMessage ?? INVALID_AUTH_MESSAGE_DEFAULT

  const requestInterceptor = async (cfg: InternalAxiosRequestConfig) => {
    if (mutex.isLocked()) {
      await mutex.waitForUnlock()
    }
    const authData = config.getAuthData()
    if (authData?.accessToken) {
      cfg.headers.Authorization = `Bearer ${authData.accessToken}`
    }
    return cfg
  }

  const responseErrorInterceptor = async (error: AxiosError) => {
    const requestConfig = error.config

    if (requestConfig?.__isAuthRetry) {
      return Promise.reject(error)
    }

    const responseCode = error.response?.status ?? 0

    // Only acquire mutex for 401s — other errors (502, timeout, abort) should
    // not block on the refresh lock.
    if (responseCode !== 401) {
      if (hasInvalidAuthMessage(error.response?.data, invalidAuthMessage)) {
        config.onAuthFailure?.({
          type: 'invalid_authentication',
          error: error.response?.data,
        })
      }
      return Promise.reject(error)
    }

    const release = await mutex.acquire()

    let retryRequestConfig: Record<string, unknown> | undefined
    try {
      const authData = config.getAuthData()
      if (!authData?.refreshToken) {
        config.onAuthFailure?.({
          type: 'no_auth_data',
          error: { code: 'no_auth_data', message: 'No auth data available' },
        })
        throw error
      }

      const newTokenHeaderValue = `Bearer ${authData.accessToken}`
      const curAuthHeaderVal = error.config?.headers?.Authorization
      if (curAuthHeaderVal !== newTokenHeaderValue) {
        const cfg = { ...error.config }
        if (!cfg.headers) throw error
        cfg.headers.Authorization = newTokenHeaderValue
        retryRequestConfig = {
          ...cfg,
          headers: cfg.headers.toJSON(),
          __isAuthRetry: true,
        }
      } else {
        const refreshResult = await config.refreshToken()
        if (!refreshResult.ok) {
          config.onAuthFailure?.({
            type: 'refresh_failed',
            error: refreshResult.data,
          })
          throw error
        }

        config.onTokenRefreshed?.(refreshResult.data)

        const updatedAuthData = config.getAuthData()
        const updatedToken = `Bearer ${updatedAuthData?.accessToken}`
        const retryConfig = { ...error.config }
        if (!retryConfig.headers) throw error
        retryConfig.headers.Authorization = updatedToken
        retryRequestConfig = {
          ...retryConfig,
          headers: retryConfig.headers.toJSON(),
          __isAuthRetry: true,
        }
      }
    } catch (refreshError) {
      if (refreshError !== error) {
        config.onAuthFailure?.({
          type: 'refresh_exception',
          error: refreshError,
        })
      }
      return await Promise.reject(error)
    } finally {
      release()
    }

    // Retry fires OUTSIDE the try/catch so transient failures (502, timeout,
    // AbortSignal) propagate to the caller, not to onAuthFailure.
    return await instance.axiosInstance.request(retryRequestConfig!)
  }

  return { requestInterceptor, responseErrorInterceptor }
}

/**
 * Creates auth interceptors and attaches them to the given apisauce instances.
 * A single shared mutex ensures only one refresh occurs across all instances.
 */
export const setAuthInterceptors = (
  instances: ApisauceInstance[],
  config: AuthInterceptorConfig,
) => {
  const sharedMutex = new Mutex()
  instances.forEach((instance) => {
    const { requestInterceptor, responseErrorInterceptor } =
      createAuthInterceptor(instance, config, sharedMutex)
    instance.axiosInstance.interceptors.request.use(requestInterceptor)
    instance.axiosInstance.interceptors.response.use(
      undefined,
      responseErrorInterceptor,
    )
  })
}
